import { useCallback, useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { ENABLE_FEATURE_FLAG_OVERRIDE_KEY, FeatureFlagIDs } from "../../models/config";
import { useFeatureFlagStore } from "../use-feature-flag-store";

import { getFeatureFlagQueryDocument } from "./feature-flag.gql";

interface UseFeatureFlagInternalProps {
  workspaceId: string;
  id: FeatureFlagIDs;
  refetch?: boolean; // force a refetch from posthog (not the same as React Query cache)
  enabled?: boolean;
  canOverride?: boolean;
  disableOverride?: boolean;
}

const useFeatureFlagInternal = ({
  workspaceId,
  id,
  refetch,
  canOverride,
  enabled,
  disableOverride,
}: UseFeatureFlagInternalProps) => {
  const { featureFlagList: featureFlagListStore } = useFeatureFlagStore();

  const query = useQuery({
    queryKey: queryKeys.featureFlags.detail(id, workspaceId),
    queryFn: async () => {
      const client = createOperationClient("GetFeatureFlag");
      const data = await client.request(getFeatureFlagQueryDocument, {
        getFeatureFlagInput: {
          workspaceId,
          id,
          refetch,
        },
      });
      return data.featureFlag;
    },
    enabled: enabled && !!workspaceId && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const getFeatureFlag = useCallback(
    (id: string) => featureFlagListStore.find((f) => f.id === id),
    [featureFlagListStore],
  );

  const featureFlag = useMemo(() => {
    const data = query.data;

    if (!data) {
      return null;
    }

    const featureFlagOverride = getFeatureFlag(id);
    const shouldOverride = !disableOverride && !!canOverride && !!featureFlagOverride;

    try {
      const payload: unknown = JSON.parse(data.payload || "{}");

      return {
        ...data,
        enabled: shouldOverride ? featureFlagOverride.enabled : data.enabled,
        payload,
      };
    } catch (_error) {
      return {
        ...data,
        enabled: shouldOverride ? featureFlagOverride.enabled : data.enabled,
        payload: null,
      };
    }
  }, [query.data, getFeatureFlag, id, canOverride, disableOverride]);

  const isEnabled = useMemo(() => {
    return featureFlag?.enabled || false;
  }, [featureFlag]);

  return {
    featureFlag,
    isEnabled,
    refetch: query.refetch,
    loading: query.isLoading,
    error: query.error,
    query,
  };
};

interface UseFeatureFlagProps extends Omit<UseFeatureFlagInternalProps, "canOverride"> {}

export const useFeatureFlag = (props: UseFeatureFlagProps) => {
  const defaultEnabled = props.enabled === undefined ? true : props.enabled;
  const { featureFlag: overrideFeatureFlag, loading: loadingOverrideFeatureFlag } = useFeatureFlagInternal({
    id: ENABLE_FEATURE_FLAG_OVERRIDE_KEY,
    workspaceId: props.workspaceId,
    canOverride: false,
    disableOverride: true,
    enabled: true,
  });

  const canOverride = overrideFeatureFlag?.enabled;
  return useFeatureFlagInternal({
    ...props,
    canOverride,
    enabled: !loadingOverrideFeatureFlag && defaultEnabled,
  });
};
